import { useMemo, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Connection,
  ConnectorProfile,
  ConnectorType,
} from '@/shared/api/generated/catalog';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { SecretField, TextField } from '@/shared/ui/Field';
import { Indicator } from '@/shared/ui/Indicator';

import { useRegisterConnection } from '../api/connections';
import { useConnectors } from '../api/connectors';
import {
  capabilityNotes,
  credentialPayload,
  defaultPortFor,
  fieldsFor,
  isRegisterable,
  missingFields,
  offersMechanismChoice,
} from '../model/registration';
import type { Mechanism } from '../model/registration';

/* Registering a source, with the credentials that source actually uses.
 *
 * **What changed and what did not.** The form used to ask for a `secret_ref` — a path inside this
 * platform's secret store — which meant every person adding a source had to understand a credential
 * layout that is none of their business. It now asks for what their DBA gave them. The security model
 * is unchanged: the values go into a POST body, the service seals them, and only the derived reference
 * is persisted.
 *
 * **The fields come from the API, not from this file.** `/v1/catalog/connectors` publishes which fields
 * each provider takes and which of them are secret, so a Databricks `token` gets a password input
 * because the server said it is a secret rather than because this file matched on a word. Five
 * hand-written forms would drift from the validator the first time a provider gained a field.
 *
 * Three things this form deliberately does with the credential values:
 *
 * * holds them in React state and nowhere else — no storage, no URL, no form `action`;
 * * clears them the instant a registration succeeds, so a second registration cannot inherit them;
 * * calls `reset()` on the mutation immediately, because `variables` on the observer is the one place
 *   a submitted credential would otherwise linger — React Query keeps it there until reset, doing
 *   exactly what it should with no idea one of the variables is a password.
 *
 * The success message is the *screen's* and not this form's: `onRegistered` closes the form, so an
 * alert rendered here would unmount in the same tick. That is not hypothetical — the first version
 * had one, and registering a source in a browser produced no confirmation whatsoever.
 */

const ENDPOINT_FALLBACK: ConnectorType = 'postgresql';

export function RegisterConnectionForm({
  onRegistered,
  step,
  connector: controlledConnector,
  onConnectorChange,
}: {
  /** Called with the registered source, so a caller can confirm it or carry its id onward. */
  readonly onRegistered: (connection: Connection) => void;
  /* Which half to render. Undefined renders both, which is how this form is used on its own; the
   * wizard passes one at a time because choosing a source type and filling its fields are two
   * decisions and the reference separates them.
   *
   * A prop rather than two components: the fields a source needs are derived from the *chosen*
   * source's profile, so splitting the file would mean the second half re-deriving state the first
   * half owns — and the credential-clearing rule below is exactly the thing that must not end up in
   * two places. */
  readonly step?: 'source' | 'credentials' | undefined;
  /** Controlled selection, so the wizard can gate its own Continue on a source being chosen. */
  /* Controlled selection. **`null` is a real value here and means "nothing chosen yet"** — it is not
   * the same as the prop being absent, which means "this form owns its own selection".
   *
   * The distinction is a bug fix rather than pedantry. With a single `ConnectorType | undefined` the
   * wizard's "nothing chosen" arrived as `undefined`, the form fell back to its internal default, and
   * PostgreSQL rendered as *selected* while the wizard still thought nothing was — so Continue stayed
   * disabled and clicking the apparently-chosen tile fired no change event, because a radio that is
   * already checked does not change. A dead first step, and it looked like a click-handling problem. */
  readonly connector?: ConnectorType | null | undefined;
  readonly onConnectorChange?: ((next: ConnectorType) => void) | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const connectors = useConnectors();
  const register = useRegisterConnection();

  const [uncontrolledConnector, setUncontrolledConnector] =
    useState<ConnectorType>(ENDPOINT_FALLBACK);
  /* Controlled when the prop is *present* — `undefined` means absent and hands control back, where
   * `null` is a controlled "nothing yet". `??` cannot express that difference, which is what the bug
   * above was. */
  const connector =
    controlledConnector === undefined ? uncontrolledConnector : controlledConnector;
  const setConnector = onConnectorChange ?? setUncontrolledConnector;
  const [mechanism, setMechanism] = useState<Mechanism>('password');
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('5432');
  const [database, setDatabase] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [attempted, setAttempted] = useState(false);
  /* Bumped whenever the secret inputs must empty. They are uncontrolled — see `SecretField`, where
   * that is a security decision — so clearing them means remounting them, and this is the key that
   * does it. A counter rather than the provider name, because a successful registration must clear
   * the fields without the provider having changed. */
  const [secretGeneration, setSecretGeneration] = useState(0);

  const profiles = connectors.data?.items ?? [];
  const profile = profiles.find((entry) => entry.connector_type === connector);

  const fields = useMemo(
    () => (profile ? fieldsFor(profile, mechanism) : []),
    [profile, mechanism],
  );
  const missing = missingFields(fields, values);

  const chooseConnector = (next: ConnectorType): void => {
    setConnector(next);
    setMechanism('password');
    /* **Cleared on every provider change, not merged.** Three providers share `username` and
     * `password`, so merging would silently carry one source's credential into another's
     * registration — the exact shape of an accidental credential reuse, performed by the UI. */
    setValues({});
    setSecretGeneration((generation) => generation + 1);
    setAttempted(false);
    const suggested = defaultPortFor(next);
    if (suggested !== null) setPort(String(suggested));
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAttempted(true);
    /* `connector` cannot be null past this line: `profile` is looked up *by* it, so a null selection
     * yields no profile and this guard has already returned. The narrowing is stated rather than
     * asserted with a non-null operator, so a future edit that decouples the two is a type error
     * here rather than a null reaching the request body. */
    if (connector === null || !profile || !isRegisterable(profile) || missing.length > 0)
      return;

    const parsedPort = Number.parseInt(port, 10);
    register.mutate(
      {
        name: name.trim(),
        connector_type: connector,
        /* A family with no endpoint still needs the three columns filled, because the row has them.
         * The credential carries the real address in that case — a Snowflake account, a Databricks
         * workspace host — so these are the connection's label rather than how it is reached, and the
         * form does not ask for them. */
        host: profile.needs_endpoint
          ? host.trim()
          : (values['host'] ?? values['account'] ?? '—'),
        port: profile.needs_endpoint && Number.isFinite(parsedPort) ? parsedPort : 443,
        database: database.trim() || (values['catalog'] ?? '—'),
        credentials: credentialPayload(fields, values),
      },
      {
        onSuccess: (connection) => {
          setName('');
          setHost('');
          setDatabase('');
          setValues({});
          setSecretGeneration((generation) => generation + 1);
          setAttempted(false);
          /* The outcome is in `registered` above, so the observer's copy of the credential can go. */
          register.reset();
          onRegistered(connection);
        },
      },
    );
  };

  if (connectors.isPending) {
    return (
      <Card title={t('connections.register.title')}>
        <p className="lq-dim">{t('connections.register.loading')}</p>
      </Card>
    );
  }

  if (connectors.isError || profiles.length === 0) {
    return (
      <Card title={t('connections.register.title')}>
        <Alert tone="error" title={t('connections.register.catalogueFailed')}>
          {t('connections.register.catalogueFailedBody')}
        </Alert>
      </Card>
    );
  }

  return (
    <Card
      title={t('connections.register.title')}
      hint={t('connections.register.subtitle')}
    >
      <div className="lq-sections">
        {step === 'credentials' ? null : (
          <section>
            <h3 className="lq-section-header">
              {t('connections.register.chooseSource')}
            </h3>
            <div className="lq-providers" role="radiogroup">
              {profiles.map((entry) => (
                <ProviderChoice
                  key={entry.connector_type}
                  profile={entry}
                  selected={entry.connector_type === connector}
                  onSelect={() => {
                    chooseConnector(entry.connector_type);
                  }}
                />
              ))}
            </div>
          </section>
        )}

        {step === 'source' ? null : (
          <form onSubmit={onSubmit}>
            <div className="lq-grid">
              <TextField
                label={t('connections.register.name')}
                hint={t('connections.register.nameHint')}
                value={name}
                onChange={setName}
                required
                maxLength={200}
              />

              {profile?.needs_endpoint === true ? (
                <>
                  <TextField
                    label={t('connections.register.host')}
                    value={host}
                    onChange={setHost}
                    required
                    maxLength={255}
                  />
                  <TextField
                    label={t('connections.register.port')}
                    type="number"
                    value={port}
                    onChange={setPort}
                    required
                    min={1}
                    max={65535}
                  />
                  <TextField
                    label={t('connections.register.database')}
                    value={database}
                    onChange={setDatabase}
                    required
                    maxLength={255}
                  />
                </>
              ) : (
                <TextField
                  label={t('connections.register.database')}
                  hint={
                    connector === 'snowflake'
                      ? t('connections.register.databaseHintSnowflake', {
                          defaultValue: 'Target database to harvest (e.g. DEMO_DB)',
                        })
                      : undefined
                  }
                  value={database}
                  onChange={setDatabase}
                  required
                  maxLength={255}
                />
              )}
            </div>

            {profile !== undefined && offersMechanismChoice(profile) ? (
              <fieldset className="lq-mechanism">
                <legend className="lq-mechanism__legend">
                  {t('connections.register.mechanism')}
                </legend>
                {(['password', 'private_key'] as const).map((option) => (
                  <label key={option} className="lq-mechanism__option">
                    <input
                      type="radio"
                      name="mechanism"
                      value={option}
                      checked={mechanism === option}
                      onChange={() => {
                        setMechanism(option);
                        /* The other mechanism's value is dropped rather than kept for a toggle back:
                         * the domain refuses a credential carrying both, so a retained value is a 422
                         * waiting for whoever flips the switch twice. */
                        setValues((current) => {
                          const next = { ...current };
                          delete next['password'];
                          delete next['private_key_pem'];
                          return next;
                        });
                        setSecretGeneration((generation) => generation + 1);
                      }}
                    />
                    <span>{t(`connections.register.mechanismOption.${option}`)}</span>
                  </label>
                ))}
              </fieldset>
            ) : null}

            <div className="lq-grid">
              {fields.map((field) => {
                const fieldLabel =
                  connector === 'snowflake' && field.name === 'password'
                    ? t('connections.field.passwordSnowflake', {
                        defaultValue: 'Personal Access Token (PAT)',
                      })
                    : t(`connections.field.${field.name}`, { defaultValue: field.name });
                const fieldHint =
                  connector === 'snowflake' && field.name === 'password'
                    ? t('connections.fieldHint.passwordSnowflake', {
                        defaultValue: 'Your Snowflake Personal Access Token (PAT)',
                      })
                    : t(`connections.fieldHint.${field.name}`, { defaultValue: '' });

                return field.secret ? (
                  <SecretField
                    key={field.name}
                    label={fieldLabel}
                    hint={fieldHint}
                    /* No `value`: see `SecretField`. The value lives in `values` for submission and the
                     * input holds it only as a DOM property, so it never reaches the serialised HTML. */
                    onChange={(value) => {
                      setValues((current) => ({ ...current, [field.name]: value }));
                    }}
                    required={field.required}
                    multiline={field.multiline}
                    resetKey={`${String(connector)}:${mechanism}:${String(secretGeneration)}`}
                  />
                ) : (
                  <TextField
                    key={field.name}
                    label={fieldLabel}
                    hint={fieldHint}
                    value={values[field.name] ?? ''}
                    onChange={(value) => {
                      setValues((current) => ({ ...current, [field.name]: value }));
                    }}
                    required={field.required}
                    maxLength={4096}
                  />
                );
              })}
            </div>

            <p className="lq-dim lq-form-note">{t('connections.register.assurance')}</p>

            {profile !== undefined && !isRegisterable(profile) ? (
              <Alert tone="notice" title={t('connections.register.unavailableTitle')}>
                {t('connections.register.unavailableBody', {
                  connector: t(`connections.connector.${profile.connector_type}`, {
                    defaultValue: profile.connector_type,
                  }),
                })}
              </Alert>
            ) : null}

            {attempted && missing.length > 0 ? (
              <Alert tone="warning" title={t('connections.register.incompleteTitle')}>
                {t('connections.register.incompleteBody', {
                  fields: missing
                    .map((field) =>
                      t(`connections.field.${field}`, { defaultValue: field }),
                    )
                    .join(', '),
                })}
              </Alert>
            ) : null}

            {register.isError ? (
              <Alert tone="error" title={t('connections.register.failed')}>
                {/* The server's own sentence, which is host-written and echoes no submitted value —
                 * proved end to end for every refusal this route can produce. Nothing is added to it
                 * here, because anything this file composed would be composed from the input. */}
                {register.error.message}
              </Alert>
            ) : null}

            <div className="lq-form-actions">
              <Button
                type="submit"
                busy={register.isPending}
                disabled={profile === undefined || !isRegisterable(profile)}
              >
                {t('connections.register.submit')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}

/** One source family, offered with what this deployment will do with it.
 *
 * A radio rather than a `<select>`: the choice carries four facts per option, and a dropdown can show
 * one line. Keyboard-operable through a real `input type="radio"` inside the label, so arrow keys move
 * within the group and no `tabIndex` juggling is needed.
 */
function ProviderChoice({
  profile,
  selected,
  onSelect,
}: {
  readonly profile: ConnectorProfile;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const available = isRegisterable(profile);

  return (
    <label
      className={[
        'lq-provider',
        selected ? 'lq-provider--selected' : '',
        available ? '' : 'lq-provider--unavailable',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        type="radio"
        name="connector"
        className="lq-provider__radio"
        value={profile.connector_type}
        checked={selected}
        onChange={onSelect}
      />
      <span className="lq-provider__name">
        {t(`connections.connector.${profile.connector_type}`, {
          defaultValue: profile.connector_type,
        })}
      </span>
      <span className="lq-provider__notes">
        {capabilityNotes(profile).map((note) => (
          <Indicator
            key={note}
            tone={
              note === 'unavailable'
                ? 'unknown'
                : note.startsWith('no')
                  ? 'neutral'
                  : 'ok'
            }
            label={t(`connections.capability.${note}`)}
          />
        ))}
      </span>
    </label>
  );
}
